import React, { useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Ban, ShieldOff, AlertTriangle, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

// Shared "Block IP / Ban account" action dialog. Reused by the SITREP Live Activity table, the
// map session-detail panel, and the "List sessions" reveal rows. IP block and account ban are
// INDEPENDENT toggles that can be combined in one action. `target`:
//   { ip, ipExpired, userId, username } — ip/username optional depending on the surface.
export default function BlockIpDialog({ target, onClose, onDone }) {
  const hasIp = !!(target?.ip) && !target?.ipExpired;
  const hasAccount = !!(target?.userId || target?.username);
  const [blockIp, setBlockIp] = useState(hasIp);
  const [banAccount, setBanAccount] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [selfConfirm, setSelfConfirm] = useState(null); // self-lockout message when blocking own IP

  if (!target) return null;
  const nothingChosen = !blockIp && !banAccount;

  const submit = async (confirmSelf = false) => {
    if (nothingChosen) { toast.error("Choose block IP, ban account, or both."); return; }
    setBusy(true);
    try {
      if (blockIp) {
        const { data } = await api.post("/admin/ip-blocks", {
          ip: target.ip,
          reason: reason.trim(),
          note: note.trim(),
          ban_user_id: banAccount ? (target.userId || undefined) : undefined,
          ban_username: banAccount && !target.userId ? (target.username || undefined) : undefined,
          confirm_self: confirmSelf,
        });
        toast.success(
          `IP ${data.ip} blocked${data.banned_username ? ` · @${data.banned_username} banned` : ""}.`);
      } else if (banAccount) {
        await api.post("/admin/ip-blocks/ban-account", {
          user_id: target.userId || undefined,
          username: !target.userId ? (target.username || undefined) : undefined,
          reason: reason.trim(),
        });
        toast.success(`@${target.username || "account"} banned.`);
      }
      onDone?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "self_ip") {
        setSelfConfirm(detail.message || "This is your current IP.");
      } else {
        toast.error(formatError(detail) || "Couldn't complete that action.");
      }
    } finally { setBusy(false); }
  };

  const Check = ({ id, checked, onChange, disabled, label, hint }) => (
    <label className={`flex items-start gap-2.5 border px-3 py-2.5 cursor-pointer transition-colors ${
      disabled ? "border-sniper opacity-50 cursor-not-allowed"
      : checked ? "border-blaze/60 bg-blaze/10" : "border-sniper hover:border-white/30"}`}>
      <input type="checkbox" checked={checked} disabled={disabled} data-testid={id}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 accent-blaze w-4 h-4 shrink-0" />
      <span className="min-w-0">
        <span className="block font-mono text-xs uppercase tracking-wide text-foreground">{label}</span>
        {hint && <span className="block font-mono text-[10px] text-muted-foreground mt-0.5 break-all">{hint}</span>}
      </span>
    </label>
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="bg-surface border-sniper text-foreground max-w-md" data-testid="block-ip-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
            <Ban className="w-5 h-5 text-blaze" /> Block / Ban
          </DialogTitle>
          <DialogDescription className="font-body text-secondary-foreground">
            Reject traffic and/or ban the account. Both are reversible from the Blocks page.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2.5">
          <Check id="block-ip-toggle" checked={blockIp} onChange={setBlockIp}
            disabled={!hasIp}
            label={hasIp ? "Block this IP (403 all requests)" : "No live IP to block"}
            hint={hasIp ? target.ip : (target?.ipExpired ? "IP past retention window" : "unknown")} />
          {hasAccount && (
            <Check id="ban-account-toggle" checked={banAccount} onChange={setBanAccount}
              label={`Ban this account`} hint={`@${target.username || target.userId}`} />
          )}

          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200}
            data-testid="block-ip-reason" placeholder="Reason (optional, logged to audit)"
            className="w-full bg-input border border-sniper focus:border-blaze outline-none px-3 py-2 text-foreground font-body text-sm transition-colors" />
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
            data-testid="block-ip-note" placeholder="Note (optional)"
            className="w-full bg-input border border-sniper focus:border-blaze outline-none px-3 py-2 text-foreground font-body text-sm transition-colors" />

          {selfConfirm && (
            <div className="border border-amber-400/50 bg-amber-400/10 p-3 flex gap-2" data-testid="block-ip-self-warning">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <p className="font-body text-[13px] text-amber-200 leading-snug">{selfConfirm}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <button onClick={onClose} disabled={busy} data-testid="block-ip-cancel"
            className="px-4 py-2 border border-sniper text-secondary-foreground hover:text-foreground font-mono uppercase text-xs transition-colors">
            Cancel
          </button>
          {selfConfirm ? (
            <button onClick={() => submit(true)} disabled={busy} data-testid="block-ip-self-confirm"
              className="px-4 py-2 bg-blaze hover:bg-blaze/80 disabled:opacity-40 text-white font-heading font-bold uppercase text-xs transition-colors inline-flex items-center gap-1.5">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldOff className="w-3.5 h-3.5" />} Yes, block my IP
            </button>
          ) : (
            <button onClick={() => submit(false)} disabled={busy || nothingChosen} data-testid="block-ip-confirm"
              className="px-4 py-2 bg-blaze hover:bg-blaze/80 disabled:opacity-40 text-white font-heading font-bold uppercase text-xs transition-colors inline-flex items-center gap-1.5">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />} Confirm
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
